import { type ReactNode } from 'react';
import MUITypography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { SxProps, Theme } from '@mui/material/styles';

import theme from 'shared/themes/default';

export const WEIGHTS = ['regular', 'medium', 'semibold', 'bold'] as const;
const ALLOWED_COMPONENTS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'li',
  'p',
  'span',
] as const;

type Weight = (typeof WEIGHTS)[number];
type AllowedComponent = (typeof ALLOWED_COMPONENTS)[number];

const getFontWeight = (weightKey?: Weight): string | number | undefined => {
  if (weightKey) {
    switch (weightKey) {
      case 'regular':
        return 'normal';
      case 'medium':
        return 500;
      case 'semibold':
        return 600;
      default:
        return weightKey;
    }
  }
  return undefined;
};

export const VARIANTS_MAPPING = {
  headingXl: 'h1',
  headingL: 'h2',
  headingM: 'h3',
  headingS: 'h4',
  headingXs: 'h5',
  subtitle: 'subtitle1',
  paragraph: 'body1',
  paragraphS: 'body2',
  helperText: 'helperText',
  helperTextS: 'helperTextS',
} as const;

export const VARIANTS = Object.keys(
  VARIANTS_MAPPING
) as (keyof typeof VARIANTS_MAPPING)[];

type Variant = keyof typeof VARIANTS_MAPPING;

interface TypographyProps {
  children?: ReactNode;
  color?: string;
  component?: AllowedComponent;
  fontWeight?: Weight;
  italic?: boolean;
  phoneVariant?: Variant;
  phoneWeight?: Weight;
  sx?: SxProps<Theme>;
  variant?: Variant;
}

const Typography = ({
  children,
  color = theme.palette.lightPure,
  fontWeight,
  italic = false,
  phoneVariant,
  phoneWeight,
  sx = {},
  variant = 'paragraph',
  ...props
}: TypographyProps) => {
  const isMobile = useMediaQuery(theme.breakpoints.mobile);
  const appliedVariant = phoneVariant && isMobile ? phoneVariant : variant;
  const appliedWeight = phoneWeight && isMobile ? phoneWeight : fontWeight;

  const sxTypography: Record<string, unknown> = {
    color,
    ...sx,
  };
  if (italic) {
    sxTypography.fontStyle = 'italic';
  }
  if (appliedWeight) {
    sxTypography.fontWeight = getFontWeight(appliedWeight);
  }

  return (
    <MUITypography
      sx={sxTypography as SxProps<Theme>}
      variant={VARIANTS_MAPPING[appliedVariant]}
      {...props}
    >
      {children}
    </MUITypography>
  );
};

export default Typography;
export type {
  TypographyProps,
  Variant as TypographyVariant,
  Weight as TypographyWeight,
};
